import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Loading } from "./components";
import OtherRoutes from "./routes/OtherRoutes";
import { setCredentials, setUserRole } from "./store/slices/authSlice";
import userTypes from "./constants/userTypes";
import { useFirebase, useJwtToken } from "./hooks";
import { useTranslation } from "react-i18next";
import { setCurrentPath, setNotificationSettings } from "./store/slices/appSlice";
import { useLazyGetNotificationSettingsQuery } from "./store/api/userNotificationSettingsApiSlice";
import { useLazyGetPreferredLanguageQuery } from "./store/api/userPreferencesApiSlice";
import { syncPreferredLanguageFromServer } from "./utils/preferredLanguage";
import { useUpdatePasswordMutation } from "./store/api/usersApiSlice";

const DoctorRoutes = lazy(() => import("./routes/DoctorRoutes"));
const PatientRoutes = lazy(() => import("./routes/PatientRoutes"));
const EmployeeRoutes = lazy(() => import("./routes/EmployeeRoutes"));
const AssistantRoutes = lazy(() => import("./routes/AssistantRoutes"));

/** Sözleşme / bilgilendirme / şifre sıfırlama – giriş yapmış olsa da OtherRoutes üzerinden açılır */
const AGREEMENT_PATHS = [
  "/forgot-password",
  "/employee/forgot-password",
  "/assistant/forgot-password",
  "/reset-password",
  "/employee/reset-password",
  "/assistant/reset-password",
  "/verify-email",
  "/user-agreement",
  "/terms-of-use",
  "/privacy-policy",
  "/gizlilik-politikasi",
  "/kullanim-kosullari",
  "/uyelik-sozlesmesi",
  "/uyelik-ve-hizmet-sozlesmesi",
  "/kisisel-pazarlama-acik-riza-metni",
  "/uzaktan-saglik-bilgilendirme",
  "/kvkk-uzaktan-saglik-bilgilendirme",
  "/uzaktan-saglik-sunumu-bilgilendirme",
  "/sozlesmeler",
  "/uzaktan-saglik-mesafeli-satis-sozlesmesi",
  "/kullanicilar-aydinlatma-metni",
  "/kvkk-politikasi",
];

const App = () => {
  //STATES----------------------
  const dispatch = useDispatch();
  const location = useLocation();
  const { token, isTokenExpired, isTokenValid, clearStore, getDecodedToken } = useJwtToken();
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setLoading] = useState(true);

  //TODO: eğer skip komutu eklenecekse, permissionlar bir şekilde localstorage'da saklanmalı.
  // const { isFetching, isLoading, isError, data } = useRefreshTokenQuery();
  const fb = useFirebase();
  const [userType, setUserType] = useState("none");

  //------------ USE EFFECT --------------
  useEffect(() => {
    fb.requestPermission();
  }, []);
  const [updatePassword] = useUpdatePasswordMutation();
  const { t } = useTranslation();
  const [getNotificationSettings] = useLazyGetNotificationSettingsQuery();
  const [getPreferredLanguage] = useLazyGetPreferredLanguageQuery();

  useEffect(() => {
    if (location.pathname !== "/login")
      dispatch(setCurrentPath(location.pathname))
  }, [location.pathname]);

  useEffect(() => {
    if (!token) {
      clearStore();
      setLoading(false);
      return;
    }

    if (isTokenExpired()) {
      clearStore();
      setLoading(false);
      return;
    }

    dispatch(setCredentials({ token }));
    const decoded = getDecodedToken();
    if (decoded?.userType) {
      dispatch(setUserRole({ name: decoded.userType.toLowerCase(), permissions: [] }));
      setUserType(decoded.userType);
    }
    setLoading(false);
  }, [token]);

  // Sayfa yüklenirken / giriş sonrası bildirim ayarlarını backend'den al, Redux'a yaz, platform izinlerini buna göre senkronize et
  useEffect(() => {
    if (!token || !isTokenValid() || isTokenExpired()) return;
    let cancelled = false;
    getNotificationSettings()
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        if (payload && typeof payload.sendSMS === "boolean") {
          const settings = {
            sendSMS: !!payload.sendSMS,
            sendMail: !!payload.sendMail,
            sendAppNotification: !!payload.sendAppNotification,
          };
          dispatch(setNotificationSettings(settings));
          if (settings.sendAppNotification) {
            fb.requestPermission();
          } else {
            fb.disableNotifications();
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token || !isTokenValid() || isTokenExpired()) return;
    syncPreferredLanguageFromServer(getPreferredLanguage).catch(() => {});
    // Yalnızca token: isTokenValid/isTokenExpired her render'da yeni referans;
    // getPreferredLanguage + i18n.changeLanguage re-render zinciri sonsuz döngüye yol açıyordu.
  }, [token]);

  //------------- RETURN --------------------------
  // Sözleşme sayfaları: giriş yapmış olsa da her zaman OtherRoutes ile açılır (404 önlenir)
  if (AGREEMENT_PATHS.includes(location.pathname))
    return <OtherRoutes />;

  // Giriş yapmamış kullanıcılar ana sayfa (/) ile uygulama bilgilerini görür; login sadece /login'de.
  // OAuth/marka doğrulama için sunucuda "/" adresinin /login'e yönlendirilmemesi gerekir.
  if (!isTokenValid() || isTokenExpired())
    return <OtherRoutes />
  if (isLoading)
    return <Loading backdrop />

  // Token var ama userType henüz belirlenmemişse loading göster
  if (token && userType === "none" && !user.userType)
    return <Loading backdrop />

  const panel = (() => {
    const stateType = String(userType || "").toUpperCase();
    const profileType = String(user.userType || "").toUpperCase();
    const type = [stateType, profileType].find((value) =>
      value === userTypes.DOCTOR ||
      value === userTypes.EMPLOYEE ||
      value === userTypes.PATIENT ||
      value === userTypes.ASSISTANT
    );
    if (type === userTypes.DOCTOR) return <DoctorRoutes />;
    if (type === userTypes.EMPLOYEE) return <EmployeeRoutes />;
    if (type === userTypes.PATIENT) return <PatientRoutes />;
    if (type === userTypes.ASSISTANT) return <AssistantRoutes />;
    return null;
  })();

  if (panel) {
    return <Suspense fallback={<Loading backdrop />}>{panel}</Suspense>;
  }

  // Hiçbir route eşleşmezse OtherRoutes'a yönlendir
  return <OtherRoutes />
};

export default App;
